import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { buttonFocusRing, COLORS } from '@/components/ui/ui.constants';

interface StepperProps {
  disabled?: boolean;
  label: string;
  max: number;
  min: number;
  value: number | null;
  onChange: (value: number | null) => void;
}

const CHEVRON_BTN = cn(
  'tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7',
  'tw-rounded-full tw-border-none tw-bg-[#f4eeff]',
  'tw-text-tci-purple hover:tw-bg-[#e8d9ff]',
  buttonFocusRing,
  'focus-visible:before:tw-rounded-full',
);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const Stepper = ({ disabled, label, max, min, value, onChange }: StepperProps) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));

  // Keep the local draft in sync when the value changes externally (e.g. stepper buttons).
  useEffect(() => {
    setDraft(value === null ? '' : String(value));
  }, [value]);

  const atMin = disabled || (value !== null && value <= min);
  const atMax = disabled || (value !== null && value >= max);

  const step = (delta: number) => {
    if (disabled) return;
    const next = value === null ? min : clamp(value + delta, min, max);
    onChange(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    setDraft(raw);
    onChange(raw === '' ? null : parseInt(raw, 10));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      step(-1);
    }
  };

  const handleBlur = () => {
    if (value === null) return;
    const clamped = clamp(value, min, max);
    setDraft(String(clamped));
    onChange(clamped);
  };

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-py-3">
      <span
        className={cn(
          'tw-font-tci tw-text-sm tw-font-semibold tw-text-tci-black',
          'tw-leading-snug',
        )}
      >
        {label}
      </span>
      <div className="tw-flex tw-items-center tw-gap-2">
        <button
          aria-disabled={atMin}
          aria-label={`Decrease ${label}`}
          className={cn(CHEVRON_BTN, atMin ? 'tw-opacity-40 tw-cursor-not-allowed' : 'tw-cursor-pointer')}
          type="button"
          onClick={atMin ? undefined : () => step(-1)}
        >
          <Icon fill={COLORS['tci-purple']} icon="ChevronLeft" size={14} />
        </button>
        <input
          aria-label={label}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={value ?? undefined}
          className={cn(
            'tw-w-14 tw-h-8 tw-text-center tw-rounded-md tw-bg-white',
            'tw-font-tci tw-text-sm tw-text-very-dark-grey tw-outline-none',
            'tw-border tw-border-solid tw-border-grey',
            buttonFocusRing,
            disabled && 'tw-opacity-50',
          )}
          disabled={disabled}
          inputMode="numeric"
          pattern="[0-9]*"
          role="spinbutton"
          type="text"
          value={draft}
          onBlur={handleBlur}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        <button
          aria-disabled={atMax}
          aria-label={`Increase ${label}`}
          className={cn(CHEVRON_BTN, atMax ? 'tw-opacity-40 tw-cursor-not-allowed' : 'tw-cursor-pointer')}
          type="button"
          onClick={atMax ? undefined : () => step(1)}
        >
          <Icon fill={COLORS['tci-purple']} icon="ChevronRight" size={14} />
        </button>
      </div>
    </div>
  );
};

export default Stepper;
